import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { DocumentNode } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import {
  JobRatesDocument,
  UpdateProjectManagementRateDocument,
  UpdateProjectManagementRateMutation,
} from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { InsightCard } from 'modules/project_management/components/InsightCard'
import EditRateCard from 'modules/project_management/components/ProfitabilityCards/EditRateCard'
import RateInfoCard from 'modules/project_management/components/ProfitabilityCards/RateInfoCard'
import { Timeline } from 'modules/project_management/components/Timeline'
import { convertProfitabilityToCents } from 'modules/project_management/lib/profitabilityCalculations'

interface EditJobRatesSidepanelProps {
  onClose: () => void
  refetchQueries: DocumentNode[]
  selectedRoleLevelInfo: {
    name: string
    id: number
    maxCostRate: number
    billingRateCents: number
    profitabilityPercentageCents: number
    currency: string
  }
  showSidePanel: boolean
}

const EditJobRatesSidepanel = ({
  onClose,
  refetchQueries,
  selectedRoleLevelInfo,
  showSidePanel,
}: EditJobRatesSidepanelProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [openNewRateCard, setOpenNewRateCard] = useState<boolean>(false)

  const { data, loading } = useQuery(JobRatesDocument, {
    variables: {
      resourceId: selectedRoleLevelInfo.id,
    },
    skip: !showSidePanel,
  })

  const [updateJobRoleRate] = useMutation(UpdateProjectManagementRateDocument, {
    extractorFn: (mutationData: UpdateProjectManagementRateMutation) =>
      mutationData.projectManagementMutations.updateProjectmanagementRate,
    awaitRefetchQueries: true,
    refetchQueries: [...new Set([JobRatesDocument, ...refetchQueries])],
  })

  const rates = data?.projectManagement?.ratesConnection?.nodes

  const handleUpdateJobRoleRate = async (values: { billRate: string; minProfitMargin: string }) => {
    track(trackingEvents.projectManagement.jobsAndRates.clickOnSaveNewJobLevelRate)

    const billingRateCents = values.billRate ? Number(values.billRate) * 100 : null
    const profitabilityPercentageCents = values.minProfitMargin
      ? convertProfitabilityToCents(Number(values.minProfitMargin))
      : null

    const activeRate = rates?.find((rate) => rate.endsOn === null)

    if (activeRate) {
      const response = await updateJobRoleRate({
        variables: {
          id: activeRate.id,
          billingRateCents,
          profitabilityPercentageCents,
          currency: selectedRoleLevelInfo.currency,
        },
      })

      const handled = handleResult(response, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        navigate(navigation.projects.jobsAndRates.fullPath())
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
    }
  }

  const handleCloseSidepanel = () => {
    setOpenNewRateCard(false)
    onClose()
  }

  return (
    <Sidepanel
      open={showSidePanel}
      onClose={handleCloseSidepanel}
      title={i18n.t('project_management.jobs_and_rates.sidepanels.edit_job_rates.title')}
      width="wide"
      boxPadding={false}
    >
      <ResourceHeader
        title={selectedRoleLevelInfo.name}
        primaryAction={{
          label: i18n.t('project_management.jobs_and_rates.sidepanels.new_rate_button'),
          icon: Plus,
          disabled: openNewRateCard,
          onClick: () => {
            setOpenNewRateCard(true)
          },
        }}
      />
      <Box paddingX="s24" paddingTop="s8" gap="s12">
        {loading ? (
          <InsightCard.Skeleton title="" withStackedBar />
        ) : (
          <Timeline>
            {openNewRateCard && (
              <EditRateCard
                areJobLevelRates
                billingRateCents={selectedRoleLevelInfo.billingRateCents / 100}
                currency={selectedRoleLevelInfo.currency}
                maxCostRate={selectedRoleLevelInfo.maxCostRate / 100}
                initialProfitMargin={selectedRoleLevelInfo.profitabilityPercentageCents}
                onClose={() => setOpenNewRateCard(false)}
                onSubmit={(values) => {
                  handleUpdateJobRoleRate(values)
                }}
              />
            )}
            {rates &&
              rates.length > 0 &&
              rates
                .slice()
                .sort((a, b) => new Date(b.startsOn).getTime() - new Date(a.startsOn).getTime())
                .map((rate, index) => (
                  <RateInfoCard
                    key={index}
                    startsOn={rate.startsOn}
                    endsOn={rate.endsOn}
                    currency={rate.currency}
                    chargingCostCents={rate.chargingCostCents}
                    billingRateCents={rate.billingRateCents}
                    profitabilityPercentageCents={rate.profitabilityPercentageCents}
                    totalCount={0}
                    areJobLevelRates
                  />
                ))}
          </Timeline>
        )}
      </Box>
    </Sidepanel>
  )
}

export default EditJobRatesSidepanel
